/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import styles from './MaActivityHeroCard.module.scss';

type TabKey = 'funding' | 'acquisitions' | 'financials' | 'ai' | 'ownership';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'funding', label: 'Funding Rounds' },
  { key: 'acquisitions', label: 'Acquisitions' },
  { key: 'financials', label: 'Financials' },
  { key: 'ai', label: 'AI Insights' },
  { key: 'ownership', label: 'Ownership' },
];

const TAB_ORDER: TabKey[] = ['funding', 'acquisitions', 'financials', 'ai', 'ownership'];
const AUTO_INTERVAL = 3200;

interface SceneProps {
  animate: boolean;
}

const FundingScene = ({ animate }: SceneProps) => (
  <>
    <div className={styles.tableTitle}>Funding Rounds</div>
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Date</th>
          <th>Round</th>
          <th>Amount</th>
          <th className={styles.hideM}>Valuation</th>
          <th>Lead investor</th>
        </tr>
      </thead>
      <tbody>
        {[
          { year: '2025', round: 'Growth Equity', amount: '$2B', val: '75.0B', lead: 'Coatue' },
          { year: '2024', round: 'Growth Equity', amount: '$500M', val: '45.0B', lead: 'Coatue' },
          { year: '2021', round: 'Series E+', amount: '$800M', val: '33.0B', lead: 'SoftBank VF2' },
          { year: '2020', round: 'Series D', amount: '$580M', val: '5.5B', lead: 'TCV' },
        ].map((row, i) => (
          <tr
            key={row.year + row.round}
            className={cn(styles.row, animate && styles.rowAnimate)}
            style={{ '--delay': `${i * 0.08}s` } as React.CSSProperties}
          >
            <td className={styles.mono}>{row.year}</td>
            <td>
              <span className={cn(styles.tag, styles.tagBlueOutline)}>{row.round}</span>
            </td>
            <td className={styles.strong}>{row.amount}</td>
            <td className={cn(styles.mono, styles.hideM)}>{row.val}</td>
            <td>{row.lead}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
);

const AcquisitionsScene = ({ animate }: SceneProps) => (
  <>
    <div className={styles.tableTitle}>Acquisitions</div>
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Date</th>
          <th>Target</th>
          <th className={styles.hideM}>Country</th>
          <th>Deal value</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {[
          {
            year: '2025',
            target: 'Cetelem Argentina',
            country: 'Argentina',
            val: '—',
            status: 'Announced',
            tag: 'tagBlue',
          },
          { year: '2025', target: 'Cetelem France', country: 'France', val: '—', status: 'Completed', tag: 'tagGreen' },
          {
            year: '2022',
            target: 'Arvog Forex Private Ltd',
            country: 'India',
            val: '$3.8M',
            status: 'Completed',
            tag: 'tagGreen',
          },
          {
            year: '2021',
            target: 'Nobly POS',
            country: 'United Kingdom',
            val: '—',
            status: 'Completed',
            tag: 'tagGreen',
          },
        ].map((row, i) => (
          <tr
            key={row.target}
            className={cn(styles.row, animate && styles.rowAnimate)}
            style={{ '--delay': `${i * 0.08}s` } as React.CSSProperties}
          >
            <td className={styles.mono}>{row.year}</td>
            <td className={styles.strong}>{row.target}</td>
            <td className={cn(styles.mono, styles.hideM)}>{row.country}</td>
            <td className={styles.mono}>{row.val}</td>
            <td>
              <span className={cn(styles.tag, styles[row.tag as keyof typeof styles])}>{row.status}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
);

const FinancialsScene = ({ animate }: SceneProps) => (
  <>
    <div className={styles.tableTitle}>Financials</div>
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Metric</th>
          <th>2024</th>
          <th className={styles.hideM}>2023</th>
          <th>YoY</th>
        </tr>
      </thead>
      <tbody>
        {[
          { metric: 'Revenue', v24: '$4.0B', v23: '$2.2B', yoy: '+72%' },
          { metric: 'Profit before tax', v24: '$1.4B', v23: '$545M', yoy: '+149%' },
          { metric: 'Net profit', v24: '$1.0B', v23: '$428M', yoy: '+134%' },
        ].map((row, i) => (
          <tr
            key={row.metric}
            className={cn(styles.row, animate && styles.rowAnimate)}
            style={{ '--delay': `${i * 0.08}s` } as React.CSSProperties}
          >
            <td className={styles.strong}>{row.metric}</td>
            <td className={styles.strong}>{row.v24}</td>
            <td className={cn(styles.mono, styles.hideM)}>{row.v23}</td>
            <td>
              <span className={cn(styles.tag, styles.tagGreen)}>{row.yoy}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className={styles.chart}>
      <div className={styles.chartLabel}>Revenue 2019–2024</div>
      <div className={styles.bars}>
        {[
          { year: '2019', h: 0.06 },
          { year: '2020', h: 0.09 },
          { year: '2021', h: 0.17 },
          { year: '2022', h: 0.28 },
          { year: '2023', h: 0.55 },
          { year: '2024', h: 1, active: true },
        ].map(({ year, h, active }, i) => (
          <div key={year} className={styles.bar}>
            <i
              className={cn(styles.barFill, active && styles.barFillActive, animate && styles.barFillAnimate)}
              style={{ '--h': h, '--delay': `${i * 0.07}s` } as React.CSSProperties}
            />
            <span>{year}</span>
          </div>
        ))}
      </div>
    </div>
  </>
);

const AiScene = ({ animate }: SceneProps) => (
  <>
    <div className={styles.tableTitle}>AI Insights</div>
    <div className={styles.aiGrid}>
      {[
        {
          title: '$75B Valuation in Secondary Share Sale',
          tag: 'Fundraising momentum',
          tagCls: 'tagBlue',
          text: 'Nov 2025 — a 67% step-up from $45B in Aug 2024.',
          cardCls: '',
        },
        {
          title: 'Revenue Surges 72% to $4B in 2024',
          tag: 'Growth signal',
          tagCls: 'tagGreen',
          text: 'Profit before tax up 149%; one million new customers every 17 days.',
          cardCls: 'aiCardGreen',
        },
        {
          title: 'UK Banking Licence in Mobilization Stage',
          tag: 'Risk factor',
          tagCls: 'tagAmber',
          text: 'Restricted since Sep 2024 — adds uncertainty to the IPO timeline.',
          cardCls: 'aiCardAmber',
        },
      ].map((card, i) => (
        <div
          key={card.title}
          className={cn(
            styles.aiCard,
            card.cardCls && styles[card.cardCls as keyof typeof styles],
            animate && styles.aiCardAnimate,
          )}
          style={{ '--delay': `${i * 0.1}s` } as React.CSSProperties}
        >
          <div className={styles.aiTop}>
            <b>{card.title}</b>
            <span className={cn(styles.tag, styles[card.tagCls as keyof typeof styles])}>{card.tag}</span>
          </div>
          <p>{card.text}</p>
        </div>
      ))}
    </div>
  </>
);

const OwnershipScene = ({ animate }: SceneProps) => (
  <>
    <div className={styles.tableTitle}>Ownership</div>
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Shareholder</th>
          <th>Type</th>
          <th className={styles.hideM}>Registry basis</th>
        </tr>
      </thead>
      <tbody>
        {[
          {
            name: 'Mono Management Ltd',
            type: 'Family office',
            basis: 'Largest shareholder of Revolut Group Holdings Ltd',
          },
          { name: 'Nikolay Storonsky', type: 'Individual', basis: 'Second largest individual shareholder' },
          { name: 'Balderton Capital', type: 'VC', basis: 'Third largest shareholder; led Series A' },
          { name: 'Index Ventures', type: 'VC', basis: 'Led Series B; holder since Seed' },
        ].map((row, i) => (
          <tr
            key={row.name}
            className={cn(styles.row, animate && styles.rowAnimate)}
            style={{ '--delay': `${i * 0.08}s` } as React.CSSProperties}
          >
            <td className={styles.strong}>{row.name}</td>
            <td>
              <span className={cn(styles.tag, styles.tagBlueOutline)}>{row.type}</span>
            </td>
            <td className={cn(styles.mono, styles.hideM)}>{row.basis}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
);

export const MaActivityHeroCard = () => {
  const cardRef = useRef<HTMLDivElement>(null);
  const inView = useIntersection(cardRef, { continuous: true, threshold: 0.3 });

  const [activeTab, setActiveTab] = useState<TabKey>('funding');
  const [animKey, setAnimKey] = useState(0);
  const [interacted, setInteracted] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const switchTab = useCallback((tab: TabKey, byUser = false) => {
    if (byUser) setInteracted(true);
    setActiveTab(tab);
    setAnimKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!inView || interacted) {
      return;
    }

    timerRef.current = setTimeout(() => {
      setActiveTab((current) => {
        const idx = TAB_ORDER.indexOf(current);
        const next = TAB_ORDER[(idx + 1) % TAB_ORDER.length];
        setAnimKey((k) => k + 1);
        return next;
      });
    }, AUTO_INTERVAL);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [inView, interacted, activeTab]);

  const SceneMap: Record<TabKey, React.FC<SceneProps>> = {
    funding: FundingScene,
    acquisitions: AcquisitionsScene,
    financials: FinancialsScene,
    ai: AiScene,
    ownership: OwnershipScene,
  };

  const Scene = SceneMap[activeTab];

  return (
    <div className={styles.card} ref={cardRef}>
      <div className={styles.header}>
        <div className={styles.avatar}>RL</div>
        <div>
          <div className={styles.companyName}>
            Revolut LTD
            <span className={styles.statusPill}>Active</span>
          </div>
          <div className={styles.companyType}>Private limited company (Ltd.)</div>
        </div>
      </div>

      <nav className={styles.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={cn(styles.tab, activeTab === tab.key && styles.tabActive)}
            onClick={() => switchTab(tab.key, true)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <b>$75B</b>
          <span>Valuation</span>
        </div>
        <div className={styles.kpi}>
          <b>$4.0B</b>
          <span>Revenue (2024)</span>
        </div>
        <div className={styles.kpi}>
          <b>$2.89B</b>
          <span>Total Funding</span>
        </div>
        <div className={styles.kpi}>
          <b>65M+</b>
          <span>Customers</span>
        </div>
        <div className={styles.kpi}>
          <b>$1.0B</b>
          <span>Net Profit</span>
        </div>
      </div>

      <div key={animKey} className={styles.scene}>
        <Scene animate={inView} />
      </div>

      <div className={styles.footer}>
        <span>
          <span className={styles.liveDot} />
          Continuously updated
        </span>
        <span>Verified against primary sources</span>
      </div>
    </div>
  );
};
